<template>
  <div class="dc-pin-overlay">
    <div class="dc-pin-card">
      <p class="dc-pin-title">
        {{ forced ? "ตั้ง PIN สำหรับปลดล็อกหน้าจอ" : "เปลี่ยน PIN" }}
      </p>
      <p v-if="forced" class="dc-pin-note">
        คลินิกเปิดใช้งานการล็อกหน้าจออัตโนมัติ กรุณาตั้ง PIN 6 หลักเพื่อใช้ปลดล็อก
      </p>

      <form autocomplete="off" @submit.prevent="submit">
        <!-- Only when the user already has a PIN. First-time enrollment asks
             for nothing else: the Bearer token is already proof of the
             account, and demanding a password would only add friction to a
             screen the user cannot skip (spec 09 §5.3). -->
        <template v-if="!forced">
          <label class="dc-pin-label" for="dc-pin-current">PIN ปัจจุบัน</label>
          <input
            id="dc-pin-current"
            v-model="currentPin"
            v-bind="pinAttrs"
            :type="pinType"
            inputmode="numeric"
            maxlength="6"
            name="dc-current-code"
            class="dc-pin-input"
            :class="{ 'dc-masked': maskWithCss }"
            :disabled="busy"
            @input="currentPin = digitsOnly(currentPin)"
          />
          <p class="dc-pin-or">หรือ</p>
          <label class="dc-pin-label" for="dc-pin-password">รหัสผ่าน</label>
          <input
            id="dc-pin-password"
            v-model="password"
            type="password"
            autocomplete="new-password"
            class="dc-pin-input"
            :disabled="busy"
          />
        </template>

        <label class="dc-pin-label" for="dc-pin-new">PIN ใหม่ (6 หลัก)</label>
        <!-- Not type="password": that is what makes Chrome and iCloud
             Passwords offer to generate a strong password into a 6-digit PIN
             field. See utils/pinField.js. -->
        <input
          id="dc-pin-new"
          ref="newPinInput"
          v-model="newPin"
          v-bind="pinAttrs"
          :type="pinType"
          inputmode="numeric"
          maxlength="6"
          name="dc-new-code"
          class="dc-pin-input"
          :class="{ 'dc-masked': maskWithCss }"
          :disabled="busy"
          @input="onNewPinInput"
        />

        <label class="dc-pin-label" for="dc-pin-confirm">ยืนยัน PIN ใหม่</label>
        <input
          id="dc-pin-confirm"
          ref="confirmPinInput"
          v-model="confirmPin"
          v-bind="pinAttrs"
          :type="pinType"
          inputmode="numeric"
          maxlength="6"
          name="dc-confirm-code"
          class="dc-pin-input"
          :class="{ 'dc-masked': maskWithCss }"
          :disabled="busy"
          @input="onConfirmPinInput"
        />

        <p v-if="error" class="dc-pin-error">{{ error }}</p>

        <button
          type="submit"
          class="dc-pin-btn"
          :disabled="busy || !canSubmit"
        >
          บันทึก PIN
        </button>
        <!-- Forced setup has no dismiss and no "later": the clinic switched
             the lock on, and a user with no PIN has nothing to unlock with. -->
        <button
          v-if="!forced"
          type="button"
          class="dc-pin-cancel"
          :disabled="busy"
          @click="$emit('close')"
        >
          ยกเลิก
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import {
  pinInputType,
  pinIgnoreAttrs,
  supportsCssTextSecurity,
} from "@/utils/pinField";

/**
 * Mirrors the server's pinPolicy.js so the user gets immediate feedback. The
 * server remains the authority — these checks are a courtesy, not a gate.
 */
const COMMON_PINS = [
  "123123",
  "121212",
  "112233",
  "696969",
  "159753",
  "147258",
  "789456",
  "142536",
  "102030",
  "123321",
];

function isSequential(pin) {
  let ascending = true;
  let descending = true;
  for (let i = 1; i < pin.length; i += 1) {
    const delta = pin.charCodeAt(i) - pin.charCodeAt(i - 1);
    if (delta !== 1) ascending = false;
    if (delta !== -1) descending = false;
  }
  return ascending || descending;
}

export function validatePinLocally(pin) {
  if (typeof pin !== "string" || !/^\d{6}$/.test(pin)) {
    return "PIN ต้องเป็นตัวเลข 6 หลัก";
  }
  if (new Set(pin).size === 1) {
    return "PIN ต้องไม่เป็นเลขซ้ำกันทั้งหมด";
  }
  if (isSequential(pin)) {
    return "PIN ต้องไม่เป็นเลขเรียงกัน";
  }
  if (COMMON_PINS.includes(pin)) {
    return "PIN นี้ใช้กันบ่อยเกินไป กรุณาเลือกใหม่";
  }
  return "";
}

export default {
  name: "PinSetupDialog",
  props: {
    // true = the clinic switched the lock on and this user has no PIN yet.
    forced: { type: Boolean, default: false },
  },
  data() {
    return {
      currentPin: "",
      password: "",
      newPin: "",
      confirmPin: "",
      error: "",
      busy: false,
      pinType: pinInputType(),
      pinAttrs: pinIgnoreAttrs(),
      maskWithCss: supportsCssTextSecurity(),
    };
  },
  computed: {
    canSubmit() {
      if (this.newPin.length !== 6 || this.confirmPin.length !== 6) return false;
      if (!this.forced && !this.currentPin && !this.password) return false;
      return true;
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.forced && this.$refs.newPinInput) this.$refs.newPinInput.focus();
    });
  },
  methods: {
    digitsOnly(value) {
      return String(value).replace(/\D/g, "").slice(0, 6);
    },
    onNewPinInput() {
      this.newPin = this.digitsOnly(this.newPin);
      this.error = "";

      // Advance rather than submit: there are two fields to fill here, so the
      // equivalent of the lock screen's auto-submit is moving the caret on.
      if (this.newPin.length === 6) {
        this.$nextTick(() => {
          if (this.$refs.confirmPinInput) this.$refs.confirmPinInput.focus();
        });
      }
    },
    onConfirmPinInput() {
      this.confirmPin = this.digitsOnly(this.confirmPin);
      this.error = "";

      // Both fields complete and nothing else outstanding — save without a
      // click, matching the lock screen.
      if (this.confirmPin.length === 6 && this.canSubmit && !this.busy) {
        this.submit();
      }
    },
    async submit() {
      if (this.busy) return;

      const localError = validatePinLocally(this.newPin);
      if (localError) {
        this.error = localError;
        return;
      }
      if (this.newPin !== this.confirmPin) {
        this.error = "PIN ทั้งสองช่องไม่ตรงกัน";
        return;
      }

      this.busy = true;
      this.error = "";
      try {
        const payload = { pin: this.newPin };
        if (!this.forced) {
          if (this.currentPin) payload.currentPin = this.currentPin;
          else payload.password = this.password;
        }
        await this.$store.dispatch("moduleUser/setOwnPin", payload);
        this.$emit("saved");
      } catch (err) {
        const status = err && err.response && err.response.status;
        if (status === 403) {
          this.error = "PIN ปัจจุบันหรือรหัสผ่านไม่ถูกต้อง";
        } else if (status === 423) {
          this.error = "บัญชีถูกล็อกชั่วคราวจากการกรอกรหัสผ่านผิดหลายครั้ง";
        } else if (status === 400) {
          this.error = "PIN นี้ใช้ไม่ได้ กรุณาเลือกใหม่";
        } else {
          this.error = "บันทึก PIN ไม่สำเร็จ กรุณาลองใหม่";
        }
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.dc-pin-overlay {
  position: fixed;
  inset: 0;
  z-index: 20001;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.92);
}

.dc-pin-card {
  width: 100%;
  max-width: 380px;
  padding: 28px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 20px 45px rgba(0, 0, 0, 0.35);
}

.dc-pin-title {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
  text-align: center;
}

.dc-pin-note {
  margin: 0 0 18px;
  font-size: 13px;
  color: #475569;
  text-align: center;
}

.dc-pin-label {
  display: block;
  margin: 12px 0 6px;
  font-size: 13px;
  color: #475569;
}

.dc-pin-or {
  margin: 10px 0 0;
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
}

.dc-pin-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 16px;
  letter-spacing: 6px;
  text-align: center;
}

.dc-pin-error {
  margin: 12px 0 0;
  font-size: 13px;
  color: #dc2626;
}

/* Applied only where CSS.supports confirmed it, so a PIN is never left
   rendering as visible digits on a browser that ignores the property. */
.dc-masked {
  -webkit-text-security: disc;
}

.dc-pin-btn {
  width: 100%;
  margin-top: 20px;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background: #2563eb;
  color: #fff;
  font-size: 15px;
  cursor: pointer;
}

.dc-pin-btn:disabled {
  background: #94a3b8;
  cursor: not-allowed;
}

.dc-pin-cancel {
  width: 100%;
  margin-top: 10px;
  padding: 8px;
  background: none;
  border: none;
  font-size: 13px;
  color: #64748b;
  cursor: pointer;
}
</style>
